import { z } from 'zod'
import type { AtlasLayout, AtlasRegionId } from '../atlasLayout'
import { atlasRegionIds } from '../atlasRegistry'
import {
  edgeTypeSchema,
  collectionSchema,
  collectionKindSchema,
  collectionStatusSchema,
  datasetCatalogueSchema,
  graphEdgeSchema,
  graphNodeSchema,
  localizedTextSchema,
  manifestSchema,
  mediaAssetSchema,
  nodeTypeSchema,
  relationshipScopeSchema,
  sourceRefSchema,
  storyAppearanceSchema,
  storyCycleSchema,
  storySchema,
  timelineChunkSchema,
  type Collection,
  type DatasetCatalogue,
  type DatasetCatalogueEntry,
  type TimelineChunk,
  type GraphNode,
  type MythDataset,
} from './schema'

const atlasRegionIdSchema = z.enum(atlasRegionIds)
const pointSchema = z.object({ x: z.number(), y: z.number() })

const atlasReservationSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  regionId: atlasRegionIdSchema,
  type: nodeTypeSchema,
  name: localizedTextSchema,
  summary: localizedTextSchema,
  plannedStoryIds: z.array(z.string()),
  sourcePlan: z.enum([
    'greek-core',
    'olympians',
    'underworld',
    'heracles',
    'argonauts',
    'norse-cosmology',
    'norse-divine-order',
    'norse-thor',
    'norse-baldr-loki',
    'norse-ragnarok',
    'norse-volsungs',
    'norse-fenrir-extension',
  ]),
  x: z.number(),
  y: z.number(),
  displayTier: z.literal(2),
})

const atlasIndexNodeSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  type: nodeTypeSchema,
  name: localizedTextSchema,
  aliases: z.array(z.string()).default([]),
  sourceRefs: z.array(z.string()).min(1),
  spoilerLevel: z.number().int().min(0).max(2),
  featured: z.boolean().default(false),
  portrait: mediaAssetSchema.optional(),
})

const atlasCollectionSummarySchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  title: localizedTextSchema,
  description: localizedTextSchema.optional(),
  kind: collectionKindSchema,
  status: collectionStatusSchema,
  defaultVariantId: z.string().regex(/^[a-z0-9-]+$/),
  variantCount: z.number().int().positive(),
  memberCount: z.number().int().positive(),
})

const atlasBootstrapSchema = z.object({
  schemaVersion: z.literal(1),
  layoutVersion: z.number().int().positive(),
  dataset: z.string(),
  manifest: manifestSchema,
  totalNodes: z.number().int().nonnegative(),
  nodes: z.array(atlasIndexNodeSchema),
  layout: z.object({
    regions: z.array(z.object({
      id: atlasRegionIdSchema,
      label: localizedTextSchema,
      anchorId: z.string(),
      center: pointSchema,
      radius: pointSchema,
      contour: z.array(pointSchema).min(6),
      nodeCount: z.number().int().nonnegative(),
      paths: z.array(z.string()).min(1),
    })),
    placements: z.array(z.object({
      id: z.string(),
      regionId: atlasRegionIdSchema,
      x: z.number(),
      y: z.number(),
      displayTier: z.union([z.literal(0), z.literal(1), z.literal(2)]),
    })),
    reservations: z.array(atlasReservationSchema),
  }),
  edgeFocus: z.record(z.string(), z.string()),
  storyCycles: z.array(storyCycleSchema),
  stories: z.array(storySchema),
  collections: z.array(atlasCollectionSummarySchema).default([]),
  sourcesPath: z.string(),
  collectionsPath: z.string().optional(),
  timelinePath: z.string().optional(),
  focusPathTemplate: z.string(),
  storyPathTemplate: z.string(),
})

const atlasRegionChunkSchema = z.object({
  schemaVersion: z.literal(1),
  regionId: atlasRegionIdSchema,
  nodes: z.array(graphNodeSchema),
  edges: z.array(z.object({
    id: z.string(),
    kind: z.literal('semantic'),
    source: z.string(),
    target: z.string(),
    type: edgeTypeSchema,
    directed: z.boolean(),
    label: localizedTextSchema,
    featured: z.boolean(),
    relationshipScope: relationshipScopeSchema,
  })),
  storyAppearances: z.array(storyAppearanceSchema),
})

const atlasTopologyChunkSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  nodeIds: z.array(z.string()),
  edges: z.array(graphEdgeSchema),
})

const atlasSourcesChunkSchema = z.object({
  schemaVersion: z.literal(1),
  sources: z.array(sourceRefSchema),
})

const atlasCollectionsChunkSchema = z.object({
  schemaVersion: z.literal(1),
  collections: z.array(collectionSchema),
})

export type AtlasBootstrap = z.infer<typeof atlasBootstrapSchema>
export type AtlasCollectionSummary = z.infer<typeof atlasCollectionSummarySchema>
export type AtlasRegionChunk = z.infer<typeof atlasRegionChunkSchema>
export type AtlasEdgeStub = AtlasRegionChunk['edges'][number]
export type AtlasTopologyChunk = z.infer<typeof atlasTopologyChunkSchema>

export interface AtlasBootstrapResult {
  atlas: AtlasBootstrap
  layout: AtlasLayout
  dataset: MythDataset
  catalogue: DatasetCatalogue
  datasetEntry: DatasetCatalogueEntry
}

const regionPromises = new Map<string, Promise<AtlasRegionChunk>>()
const focusPromises = new Map<string, Promise<AtlasTopologyChunk>>()
const storyPromises = new Map<string, Promise<AtlasTopologyChunk>>()
const bootstrapPromises = new Map<string, Promise<AtlasBootstrapResult>>()
const sourcesPromises = new Map<string, Promise<MythDataset['sources']>>()
const collectionsPromises = new Map<string, Promise<Collection[]>>()
const timelinePromises = new Map<string, Promise<TimelineChunk>>()
let cataloguePromise: Promise<DatasetCatalogue> | undefined

async function readAtlasJson(path: string) {
  const response = await fetch(`${import.meta.env.BASE_URL}data/${path}`)
  if (!response.ok) throw new Error(`Unable to load ${path}`)
  return response.json()
}

function toStubNode(node: AtlasBootstrap['nodes'][number]): GraphNode {
  return {
    id: node.id,
    type: node.type,
    name: node.name,
    summary: node.name,
    aliases: node.aliases,
    sourceRefs: node.sourceRefs,
    spoilerLevel: node.spoilerLevel,
    featured: node.featured,
    media: node.portrait,
    mediaSet: node.portrait ? { graphPortrait: node.portrait, inspectorHero: node.portrait, historicalRepresentations: [] } : undefined,
    contentStatus: 'stub',
    adaptations: [],
  }
}

export function loadDatasetCatalogue(): Promise<DatasetCatalogue> {
  cataloguePromise ??= readAtlasJson('datasets.json').then((raw) => datasetCatalogueSchema.parse(raw)).catch((error) => {
    cataloguePromise = undefined
    throw error
  })
  return cataloguePromise
}

function datasetEntryFor(catalogue: DatasetCatalogue, datasetId?: string) {
  const requested = datasetId ? catalogue.datasets.find((entry) => entry.id === datasetId) : undefined
  return requested ?? catalogue.datasets.find((entry) => entry.id === catalogue.defaultDatasetId) ?? catalogue.datasets[0]
}

export function loadAtlasBootstrap(datasetId?: string): Promise<AtlasBootstrapResult> {
  const requestKey = datasetId ?? '__default__'
  const existing = bootstrapPromises.get(requestKey)
  if (existing) return existing
  const request = loadDatasetCatalogue().then(async (catalogue) => {
    const datasetEntry = datasetEntryFor(catalogue, datasetId)
    const raw = await readAtlasJson(datasetEntry.bootstrapPath)
    const atlas = atlasBootstrapSchema.parse(raw)
    const layout: AtlasLayout = {
      regions: atlas.layout.regions.map(({ paths: _paths, ...region }) => region),
      placements: atlas.layout.placements,
      reservations: atlas.layout.reservations,
    }
    return {
      atlas,
      layout,
      dataset: {
        manifest: atlas.manifest,
        nodes: atlas.nodes.map(toStubNode),
        edges: [],
        sources: [],
        storyCycles: atlas.storyCycles,
        stories: atlas.stories,
        storyAppearances: [],
        collections: [],
      },
      catalogue,
      datasetEntry,
    }
  }).catch((error) => {
    bootstrapPromises.delete(requestKey)
    throw error
  })
  bootstrapPromises.set(requestKey, request)
  return request
}

export function loadAtlasRegion(atlas: AtlasBootstrap, regionId: AtlasRegionId) {
  const cacheKey = `${atlas.dataset}:${regionId}`
  const existing = regionPromises.get(cacheKey)
  if (existing) return existing
  const paths = atlas.layout.regions.find((region) => region.id === regionId)?.paths
  if (!paths) return Promise.reject(new Error(`Unknown Atlas region: ${regionId}`))
  const request = Promise.all(paths.map((path) => readAtlasJson(path).then((raw) => atlasRegionChunkSchema.parse(raw))))
    .then((chunks) => ({
      schemaVersion: 1 as const,
      regionId,
      nodes: mergeById([], chunks.flatMap((chunk) => chunk.nodes)),
      edges: mergeById([], chunks.flatMap((chunk) => chunk.edges)),
      storyAppearances: mergeById([], chunks.flatMap((chunk) => chunk.storyAppearances)),
    }))
    .catch((error) => {
    regionPromises.delete(cacheKey)
    throw error
  })
  regionPromises.set(cacheKey, request)
  return request
}

function resolveTemplate(template: string, id: string) {
  return template.replace('{id}', encodeURIComponent(id))
}

export function loadAtlasFocus(atlas: AtlasBootstrap, nodeId: string) {
  const cacheKey = `${atlas.dataset}:${nodeId}`
  const existing = focusPromises.get(cacheKey)
  if (existing) return existing
  const request = readAtlasJson(resolveTemplate(atlas.focusPathTemplate, nodeId)).then((raw) => atlasTopologyChunkSchema.parse(raw)).catch((error) => {
    focusPromises.delete(cacheKey)
    throw error
  })
  focusPromises.set(cacheKey, request)
  return request
}

export function loadAtlasStory(atlas: AtlasBootstrap, storyId: string) {
  const cacheKey = `${atlas.dataset}:${storyId}`
  const existing = storyPromises.get(cacheKey)
  if (existing) return existing
  const request = readAtlasJson(resolveTemplate(atlas.storyPathTemplate, storyId)).then((raw) => atlasTopologyChunkSchema.parse(raw)).catch((error) => {
    storyPromises.delete(cacheKey)
    throw error
  })
  storyPromises.set(cacheKey, request)
  return request
}

export function loadAtlasSources(atlas: AtlasBootstrap) {
  const existing = sourcesPromises.get(atlas.dataset)
  if (existing) return existing
  const request = readAtlasJson(atlas.sourcesPath).then((raw) => atlasSourcesChunkSchema.parse(raw).sources).catch((error) => {
    sourcesPromises.delete(atlas.dataset)
    throw error
  })
  sourcesPromises.set(atlas.dataset, request)
  return request
}

export function loadAtlasCollections(atlas: AtlasBootstrap) {
  if (!atlas.collectionsPath) return Promise.resolve([] as Collection[])
  const existing = collectionsPromises.get(atlas.dataset)
  if (existing) return existing
  const request = readAtlasJson(atlas.collectionsPath).then((raw) => atlasCollectionsChunkSchema.parse(raw).collections).catch((error) => {
    collectionsPromises.delete(atlas.dataset)
    throw error
  })
  collectionsPromises.set(atlas.dataset, request)
  return request
}

export function loadAtlasTimeline(atlas: AtlasBootstrap) {
  if (!atlas.timelinePath) return Promise.reject(new Error('This mythology dataset does not have a public timeline yet.'))
  const existing = timelinePromises.get(atlas.dataset)
  if (existing) return existing
  const request = readAtlasJson(atlas.timelinePath).then((raw) => timelineChunkSchema.parse(raw)).catch((error) => {
    timelinePromises.delete(atlas.dataset)
    throw error
  })
  timelinePromises.set(atlas.dataset, request)
  return request
}

function mergeById<T extends { id: string }>(existing: readonly T[], incoming: readonly T[]) {
  const merged = new Map(existing.map((item) => [item.id, item]))
  incoming.forEach((item) => merged.set(item.id, item))
  return [...merged.values()]
}

export function mergeAtlasDataset(
  dataset: MythDataset,
  options: {
    regions?: readonly AtlasRegionChunk[]
    topologies?: readonly AtlasTopologyChunk[]
    sources?: MythDataset['sources']
    collections?: MythDataset['collections']
  },
): MythDataset {
  const regionNodes = options.regions?.flatMap((region) => region.nodes) ?? []
  const appearances = options.regions?.flatMap((region) => region.storyAppearances) ?? []
  const edges = options.topologies?.flatMap((topology) => topology.edges) ?? []
  return {
    ...dataset,
    nodes: mergeById(dataset.nodes, regionNodes),
    edges: mergeById(dataset.edges, edges),
    sources: options.sources ? mergeById(dataset.sources, options.sources) : dataset.sources,
    storyAppearances: mergeById(dataset.storyAppearances, appearances),
    collections: options.collections ? mergeById(dataset.collections, options.collections) : dataset.collections,
  }
}

export function mergeAtlasEdges(existing: readonly AtlasEdgeStub[], incoming: readonly AtlasEdgeStub[]) {
  return mergeById(existing, incoming)
}

export function regionsForNodeIds(atlas: AtlasBootstrap, nodeIds: readonly string[]) {
  const placementById = new Map(atlas.layout.placements.map((placement) => [placement.id, placement.regionId]))
  return [...new Set(nodeIds.map((id) => placementById.get(id)).filter((id): id is AtlasRegionId => Boolean(id)))]
}

export function focusNodeIdFor(atlas: AtlasBootstrap, focusId: string) {
  return atlas.edgeFocus[focusId] ?? focusId
}

export function resetAtlasLoaderCacheForTests() {
  cataloguePromise = undefined
  bootstrapPromises.clear()
  sourcesPromises.clear()
  collectionsPromises.clear()
  timelinePromises.clear()
  regionPromises.clear()
  focusPromises.clear()
  storyPromises.clear()
}
